/**
 * `AppData`: top-of-tree singleton for state that outlives any individual
 * `Datastore` instance.
 *
 * One `AppData` is constructed at app boot and threaded down via
 * `AppDataContext`. It owns things that must survive instance switches:
 * notification toasts (with subscribe/notify so the toast renderer can
 * re-render), the per-instance session-data notification channels keyed by
 * structure+instance, the post-auth redirect target, and a CSRF token. Toasts
 * are deduped by `id` and auto-removed after `duration` ms.
 *
 * Add new cross-instance state here rather than on `Datastore`, which is torn
 * down on instance change.
 */

import { SharedData } from './shareddata';
import type { FormatParams, NotificationToast, TLabel } from './stdtypes';
import { generateRandomKey } from './util';

export class AppData {
    // put any global things you need to access in here rather than using
    // globals. This can be used for sessionData, shared react context,
    // active-popup tracking, and other things.
    private notificationToasts: NotificationToast[] = [];
    private notificationToastListeners: Set<() => void> = new Set();

    addNotificationToast(notificationToast: NotificationToast) {
        // If a toast with this ID already exists, don't add a duplicate
        if (notificationToast.id && this.notificationToasts.some((t) => t.id === notificationToast.id)) {
            return;
        }
        this.notificationToasts = [...this.notificationToasts, notificationToast];
        this.notifyNotificationToastListeners();
    }

    removeNotificationToast(id: string) {
        this.notificationToasts = [...this.notificationToasts.filter((toast) => toast.id !== id)];
        this.notifyNotificationToastListeners();
    }
    getNotificationToasts(): NotificationToast[] {
        return this.notificationToasts || [];
    }
    subscribeToNotificationToasts(callback: () => void): () => void {
        this.notificationToastListeners.add(callback);
        return () => {
            this.notificationToastListeners.delete(callback);
        };
    }
    private notifyNotificationToastListeners() {
        this.notificationToastListeners.forEach((callback) => callback());
    }

    sharedDataMap: Record<string, Record<string, SharedData>> = {};
    getSharedData(structureKey: string, instanceKey: string): SharedData {
        if (!this.sharedDataMap[structureKey]) {
            this.sharedDataMap[structureKey] = {};
        }
        if (!this.sharedDataMap[structureKey][instanceKey]) {
            this.sharedDataMap[structureKey][instanceKey] = new SharedData();
        }
        return this.sharedDataMap[structureKey][instanceKey];
    }

    gotoAfterAuth: { structureKey: string; instanceKey: string; screenKey?: string; params: any } | null = null;
    setGotoAfterAuth(gotoAfterAuth: { structureKey: string; instanceKey: string; screenKey?: string; params: any }) {
        this.gotoAfterAuth = gotoAfterAuth;
    }
    getGotoAfterAuth() {
        return this.gotoAfterAuth;
    }
    clearGotoAfterAuth() {
        this.gotoAfterAuth = null;
    }

    csrfToken: string | null = null;
    getCSRFToken() {
        if (!this.csrfToken) {
            this.csrfToken = this._generateCSRFToken();
        }
        return this.csrfToken;
    }
    isValidCSRFToken(csrfToken: string) {
        return csrfToken && this.csrfToken === csrfToken;
    }
    _generateCSRFToken() {
        return generateRandomKey(16);
    }
    clearCSRFToken() {
        this.csrfToken = null;
    }
}

export type AddNotificationToastProps = Omit<NotificationToast, 'id'> & {
    appData: AppData;
    id?: string;
    type: 'info' | 'success' | 'error';
    duration?: number;
    label: TLabel;
    formatParams?: FormatParams;
    dismissable?: boolean;
};

export function addNotificationToast({
    appData,
    id,
    type,
    duration = 7000,
    label,
    dismissable = true,
    formatParams,
}: AddNotificationToastProps) {
    const toastId = id ?? generateRandomKey(12);
    appData.addNotificationToast({ id: toastId, type, label, dismissable, formatParams });
    if (duration !== undefined) {
        setTimeout(() => {
            appData.removeNotificationToast(toastId);
        }, duration);
    }
}
